import { useState, useEffect, useCallback, useRef } from "react";
import { useSectionBg } from "@/hooks/useSectionBg";
import { supabase } from "@/lib/supabase";

const VISIBLE_CARDS = 3;
const AUTO_PLAY_INTERVAL = 4500;

interface RealReview {
  id: string;
  reviewer_name: string;
  rating: number;
  review_text: string;
  is_verified: boolean;
  created_at: string;
  product_id: number;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i
          key={i}
          className={`ri-star-fill text-sm ${i < rating ? "text-amber-400" : "text-gray-700"}`}
        />
      ))}
    </div>
  );
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("ar-SA", { year: "numeric", month: "long" });
  } catch {
    return "";
  }
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4">
      <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-400/10 border border-amber-400/20">
        <i className="ri-chat-smile-3-line text-amber-400 text-2xl" />
      </div>
      <p
        className="text-gray-400 text-base text-center"
        style={{ fontFamily: "'Cairo', sans-serif" }}
      >
        لا توجد تقييمات بعد — كن أول من يشارك تجربته!
      </p>
    </div>
  );
}

export default function ReviewsSection() {
  const { bg: sectionBg } = useSectionBg('sectionReviews');
  const [reviews, setReviews] = useState<RealReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<"left" | "right">("left");
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const fetchReviews = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("reviews")
        .select("id, reviewer_name, rating, review_text, is_verified, created_at, product_id")
        .order("created_at", { ascending: false });
      setReviews((data as RealReview[]) ?? []);
      setLoading(false);
    };
    fetchReviews();
  }, []);

  const total = reviews.length;
  const maxIndex = Math.max(0, total - VISIBLE_CARDS);

  const goTo = useCallback(
    (idx: number, dir: "left" | "right" = "left") => {
      if (isAnimating) return;
      setDirection(dir);
      setIsAnimating(true);
      setTimeout(() => {
        setCurrent(idx);
        setIsAnimating(false);
      }, 350);
    },
    [isAnimating]
  );

  const goNext = useCallback(() => {
    const next = current >= maxIndex ? 0 : current + 1;
    goTo(next, "left");
  }, [current, maxIndex, goTo]);

  const goPrev = useCallback(() => {
    const prev = current <= 0 ? maxIndex : current - 1;
    goTo(prev, "right");
  }, [current, maxIndex, goTo]);

  useEffect(() => {
    if (isPaused || total <= VISIBLE_CARDS) return;
    intervalRef.current = setInterval(goNext, AUTO_PLAY_INTERVAL);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [goNext, isPaused, total]);

  const visibleReviews = reviews.slice(current, current + VISIBLE_CARDS);

  const overallRating = total
    ? (reviews.reduce((acc, r) => acc + r.rating, 0) / total).toFixed(1)
    : "0.0";

  const ratingCounts = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length,
  }));

  return (
    <section
      id="reviews"
      dir="rtl"
      className="py-24 px-4 relative overflow-hidden"
      style={{ background: sectionBg }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[1px] bg-gradient-to-r from-transparent via-amber-400/20 to-transparent" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[1px] bg-gradient-to-r from-transparent via-amber-400/20 to-transparent" />
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-amber-400/3 blur-3xl" />
        <div className="absolute bottom-20 left-10 w-64 h-64 rounded-full bg-amber-400/3 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <span
            className="inline-block text-xs font-bold tracking-[0.2em] text-amber-400 uppercase border border-amber-400/30 rounded-full px-4 py-1.5 mb-5"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            آراء العملاء
          </span>
          <h2
            className="text-3xl md:text-4xl font-black text-white mb-3"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            ماذا يقول عملاؤنا؟
          </h2>
          <p
            className="text-gray-500 text-sm max-w-lg mx-auto"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            تقييمات حقيقية من عملائنا بعد شراء منتجاتنا
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
          </div>
        ) : total === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Stats bar */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mb-14">
              <div className="flex flex-col items-center">
                <span className="text-5xl font-black text-amber-400" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {overallRating}
                </span>
                <StarRating rating={Math.round(parseFloat(overallRating))} />
                <span className="text-gray-500 text-xs mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  من {total} مراجعة موثقة
                </span>
              </div>

              <div className="hidden sm:block w-px h-20 bg-white/10" />

              <div className="flex flex-col gap-1.5 min-w-[180px]">
                {ratingCounts.map(({ star, count }) => (
                  <div key={star} className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-3" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      {star}
                    </span>
                    <i className="ri-star-fill text-amber-400 text-xs" />
                    <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-l from-amber-400 to-amber-600 rounded-full transition-all duration-700"
                        style={{ width: `${total ? (count / total) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-600 w-4">{count}</span>
                  </div>
                ))}
              </div>

              <div className="hidden sm:block w-px h-20 bg-white/10" />

              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-emerald-500/10 border border-emerald-500/30">
                  <i className="ri-shield-check-fill text-emerald-400 text-xl" />
                </div>
                <div className="text-center">
                  <span className="block text-white font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    100% موثق
                  </span>
                  <span className="block text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    مراجعات حقيقية
                  </span>
                </div>
              </div>
            </div>

            {/* Slider */}
            <div className="relative">
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                style={{
                  opacity: isAnimating ? 0 : 1,
                  transform: isAnimating
                    ? `translateX(${direction === "left" ? "-20px" : "20px"})`
                    : "translateX(0)",
                  transition: "opacity 0.35s ease, transform 0.35s ease",
                }}
              >
                {visibleReviews.map((review) => (
                  <div
                    key={review.id}
                    className="bg-[#111111] border border-white/6 rounded-2xl p-6 flex flex-col gap-4 hover:border-amber-400/20 transition-all duration-300 group relative overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-400/3 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl pointer-events-none" />

                    {/* Top: avatar + name + rating */}
                    <div className="flex items-start gap-3 relative z-10">
                      <div className="relative shrink-0">
                        <div className="w-12 h-12 rounded-full bg-amber-400/10 border-2 border-amber-400/20 flex items-center justify-center">
                          <i className="ri-user-3-fill text-amber-400 text-xl" />
                        </div>
                        {review.is_verified && (
                          <span className="absolute -bottom-0.5 -left-0.5 w-5 h-5 flex items-center justify-center bg-emerald-500 rounded-full border-2 border-[#111]">
                            <i className="ri-check-fill text-white text-[9px]" />
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className="text-white font-bold text-sm leading-tight truncate"
                            style={{ fontFamily: "'Cairo', sans-serif" }}
                          >
                            {review.reviewer_name}
                          </span>
                          <StarRating rating={review.rating} />
                        </div>
                        <span
                          className="text-gray-500 text-xs mt-0.5 block"
                          style={{ fontFamily: "'Cairo', sans-serif" }}
                        >
                          {formatDate(review.created_at)}
                        </span>
                      </div>
                    </div>

                    {/* Quote */}
                    <div className="relative z-10">
                      <i className="ri-double-quotes-l text-amber-400/20 text-3xl absolute -top-1 right-0 leading-none" />
                      <p
                        className="text-gray-400 text-sm leading-relaxed pt-4"
                        style={{ fontFamily: "'Cairo', sans-serif" }}
                      >
                        {review.review_text}
                      </p>
                    </div>

                    {/* Bottom: verified badge */}
                    {review.is_verified && (
                      <div className="flex items-center justify-end mt-auto pt-3 border-t border-white/5 relative z-10">
                        <span
                          className="inline-flex items-center gap-1.5 text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full px-3 py-1 whitespace-nowrap"
                          style={{ fontFamily: "'Cairo', sans-serif" }}
                        >
                          <i className="ri-shield-check-fill text-[10px]" />
                          مشتري موثق
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Navigation arrows */}
              {total > VISIBLE_CARDS && (
                <>
                  <button
                    onClick={goNext}
                    className="absolute -left-5 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-[#1a1a1a] border border-white/10 hover:border-amber-400/40 hover:text-amber-400 text-gray-400 rounded-full transition-all cursor-pointer hidden lg:flex"
                    aria-label="السابق"
                  >
                    <i className="ri-arrow-left-s-line text-lg" />
                  </button>
                  <button
                    onClick={goPrev}
                    className="absolute -right-5 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-[#1a1a1a] border border-white/10 hover:border-amber-400/40 hover:text-amber-400 text-gray-400 rounded-full transition-all cursor-pointer hidden lg:flex"
                    aria-label="التالي"
                  >
                    <i className="ri-arrow-right-s-line text-lg" />
                  </button>
                </>
              )}
            </div>

            {/* Dots */}
            {total > VISIBLE_CARDS && (
              <div className="flex items-center justify-center gap-2 mt-10">
                {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goTo(i, i > current ? "left" : "right")}
                    className={`rounded-full transition-all duration-300 cursor-pointer ${
                      i === current
                        ? "w-6 h-2 bg-amber-400"
                        : "w-2 h-2 bg-white/15 hover:bg-white/30"
                    }`}
                    aria-label={`الشريحة ${i + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Mobile arrows */}
            {total > VISIBLE_CARDS && (
              <div className="flex items-center justify-center gap-4 mt-6 lg:hidden">
                <button
                  onClick={goPrev}
                  className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a] border border-white/10 hover:border-amber-400/40 hover:text-amber-400 text-gray-400 rounded-full transition-all cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-lg" />
                </button>
                <button
                  onClick={goNext}
                  className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a] border border-white/10 hover:border-amber-400/40 hover:text-amber-400 text-gray-400 rounded-full transition-all cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-lg" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
